import LoadingSpinner from '@/components/ui/loading'
import { useAuthContext, useSubscriptionContext } from '@/context'
import type { ReactElement, ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

type Props = {
    component: ReactNode
}

function isActiveSubscription(sub: { active?: boolean; status?: string } | undefined): boolean {
    if (!sub) return false
    if (sub.active === true) return true
    return sub.status === 'active' || sub.status === 'trialing'
}

const SubscriptionRouteWrapper = ({ component }: Props): ReactElement => {
    const location = useLocation()
    const { loggedIn, isLoading, user } = useAuthContext()
    const { subscription } = useSubscriptionContext()

    // This wrapper assumes auth is required (used for /dashboard). If auth is still
    // resolving, show a single stable loading state to avoid redirect flicker.
    if (loggedIn && isLoading) {
        return <LoadingSpinner />
    }

    // If the user isn't logged in, defer to the normal auth gating.
    if (!loggedIn) {
        return <>{component}</>
    }

    // Demo / unactivated accounts historically have been allowed into the app (with upgrade prompts).
    // Keep that behavior stable while still paywalling fully-active accounts without subscription.
    if (user?.active_account === false) {
        return <>{component}</>
    }

    // Admin/staff users should never be blocked by Workspaces subscription gating.
    // This prevents redirect loops during local/dev and internal admin access.
    const isAdminUser = Boolean(user?.isAdmin) || Boolean(user?.email?.toLowerCase().endsWith('@500rockets.io'))
    if (isAdminUser) {
        return <>{component}</>
    }

    // Mobile-consumer funnel: allow access without subscription while the free window is active.
    // If the backend flags the window as expired, enforce upgrade via /subscribe.
    if (user?.experience_variant === 'mobile_consumer') {
        if ((user as any)?.capabilities?.requires_subscription) {
            const redirectPath = encodeURIComponent(location.pathname + location.search)
            return <Navigate to={`/subscribe?upgrade=true&redirect_url=${redirectPath}`} replace />
        }
        return <>{component}</>
    }

    const subscribed = isActiveSubscription(subscription)
    if (subscribed) {
        return <>{component}</>
    }

    const redirectPath = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/subscribe?upgrade=true&redirect_url=${redirectPath}`} replace />
}

export default SubscriptionRouteWrapper

